import React, { useState, useEffect } from 'react';
import { X, Bell, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../../utils/api';
import ScheduleBuilder from './ScheduleBuilder';

function CreateNotificationModal({ onClose, onSuccess, phones, accounts }) {
  const [form, setForm] = useState({
    title: '',
    phoneId: '',
    bankAccountId: '',
    frequency: 'daily',
    time: '09:00',
    byweekday: [],
    bymonthday: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState('');

  function validateForm() {
    const newErrors = {};
    
    if (!form.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!form.bankAccountId) {
      newErrors.bankAccountId = 'Please select a bank account';
    }
    
    if (!form.phoneId) {
      newErrors.phoneId = 'Please select a phone number';
    }
    
    if (!form.time) {
      newErrors.time = 'Please select a time';
    }
    
    if (form.frequency === 'weekly' && form.byweekday.length === 0) {
      newErrors.byweekday = 'Please select at least one day of the week';
    }
    
    if (form.frequency === 'monthly' && form.bymonthday.length === 0) {
      newErrors.bymonthday = 'Please select at least one day of the month';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setGeneralError('');
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      // Create startDateTime from time
      const [hours, minutes] = form.time.split(':').map(Number);
      const dt = new Date();
      dt.setHours(hours, minutes, 0, 0);
      
      // If time is in the past today, use tomorrow
      if (dt <= new Date()) {
        dt.setDate(dt.getDate() + 1);
      }

      const payload = {
        title: form.title.trim(),
        phoneId: form.phoneId,
        bankAccountId: form.bankAccountId,
        frequency: form.frequency,
        startDateTime: dt.toISOString(),
        time: form.time,
        timeOfDay: form.time,
        interval: 1,
        weekdays: form.frequency === 'weekly' ? convertWeekdays(form.byweekday) : undefined,
        monthdays: form.frequency === 'monthly' ? form.bymonthday : undefined,
        tzMode: 'floating',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      await api.post('/api/notifications/create', payload);
      onSuccess();
    } catch (err) {
      console.error('Error creating notification:', err);
      setGeneralError(err.response?.data?.error || 'Failed to create notification');
    } finally {
      setSubmitting(false);
    }
  }

  // Convert UI format (0-6, Sun-Sat) to database format (1-7, Mon-Sun)
  function convertWeekdays(uiWeekdays) {
    return uiWeekdays.map(day => day === 0 ? 7 : day);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 flex-shrink-0">
              <Bell className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Create Notification</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {generalError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-700">{generalError}</span>
            </div>
          )}

          {/* Check for missing prerequisites */}
          {phones.length === 0 && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-amber-900">No phone numbers added</div>
                  <div className="text-sm text-amber-800 mt-1">
                    You need to add a phone number first.{' '}
                    <a href="/dashboard/phones" className="underline hover:text-amber-900">
                      Add phone →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {accounts.length === 0 && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium text-amber-900">No bank accounts connected</div>
                  <div className="text-sm text-amber-800 mt-1">
                    You need to connect a bank account first.{' '}
                    <a href="/dashboard/banks" className="underline hover:text-amber-900">
                      Connect bank →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2">
                Notification Title
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g., Checking Account Balance"
                className={`w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.title ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.title && (
                <p className="text-sm text-red-600 mt-1">{errors.title}</p>
              )}
            </div>

            {/* Bank Account */}
            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2">
                Bank Account
              </label>
              <select
                value={form.bankAccountId}
                onChange={(e) => setForm({ ...form, bankAccountId: e.target.value })}
                className={`w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.bankAccountId ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={accounts.length === 0}
              >
                <option value="">Select a bank account</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name || account.bank_account_name || 'Account'}
                    {account.mask ? ` (****${account.mask})` : ''}
                  </option>
                ))}
              </select>
              {errors.bankAccountId && (
                <p className="text-sm text-red-600 mt-1">{errors.bankAccountId}</p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <select
                value={form.phoneId}
                onChange={(e) => setForm({ ...form, phoneId: e.target.value })}
                className={`w-full px-3 py-2 text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.phoneId ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={phones.length === 0}
              >
                <option value="">Select a phone number</option>
                {phones.map((phone) => (
                  <option key={phone.id} value={phone.id}>
                    {phone.phone_name} ({phone.phone_number})
                  </option>
                ))}
              </select>
              {errors.phoneId && (
                <p className="text-sm text-red-600 mt-1">{errors.phoneId}</p>
              )}
            </div>

            {/* Schedule Builder */}
            <div className="pt-2 border-t border-gray-200">
              <ScheduleBuilder form={form} onChange={setForm} />
              {(errors.byweekday || errors.bymonthday) && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.byweekday || errors.bymonthday}
                </p>
              )}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 sm:py-2 px-4 min-h-[44px] border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || phones.length === 0 || accounts.length === 0}
            className="flex-1 py-2.5 sm:py-2 px-4 min-h-[44px] bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Creating...' : 'Create Notification'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateNotificationModal;
